import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getAllPosts, deletePost, type BlogArticle } from '@/hooks/useBlogStore';
import { articles as mockArticles } from '@/mocks/blog';

export default function BlogAdmin() {
  const [posts, setPosts] = useState<BlogArticle[]>(mockArticles);
  const [deleting, setDeleting] = useState<number | null>(null);

  const loadPosts = useCallback(async () => {
    try {
      const all = await getAllPosts();
      setPosts(all);
    } catch (err) {
      console.error('Admin load failed:', err);
      // mockArticles already in state
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    setDeleting(id);
    try {
      await deletePost(id);
      await loadPosts();
    } catch (err) {
      console.error('Delete failed:', err);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-primary-500">
                Blog Admin
              </h1>
              <p className="text-foreground-600 mt-1 font-body">
                Manage your blog posts, create new articles, and edit existing content.
              </p>
            </div>
            <Link
              to="/blog/admin/new"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 bg-primary-500 text-background-50 hover:bg-primary-600 h-11 rounded-lg text-sm px-6 cursor-pointer"
            >
              <i className="ri-add-line" />
              New Post
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-article-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-600 font-body mb-4">No blog posts yet.</p>
              <Link
                to="/blog/admin/new"
                className="inline-flex items-center gap-2 font-label font-medium text-secondary-500 hover:text-secondary-600 transition-colors"
              >
                <i className="ri-add-line" />
                Create your first post
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={post.image}
                      alt={post.imageAlt}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-label font-medium text-accent-700">
                        {post.categoryLabel}
                      </span>
                      {post.isPremium && (
                        <span className="text-xs font-label text-foreground-500 flex items-center gap-1">
                          <i className="ri-lock-line" />
                          Premium
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 truncate">
                      {post.title}
                    </h3>
                    <p className="text-xs font-label text-foreground-500 mt-0.5">
                      {new Date(post.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                      {' · '}
                      {post.readTime}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link
                      to={`/blog/${post.slug}?ref=admin`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-label text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-background-200/50"
                    >
                      <i className="ri-eye-line" />
                      Preview
                    </Link>
                    <Link
                      to={`/blog/admin/${post.id}`}
                      className="inline-flex items-center gap-1.5 font-label text-sm font-medium text-secondary-500 hover:text-secondary-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-secondary-100/50"
                    >
                      <i className="ri-edit-line" />
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(post.id)}
                      disabled={deleting === post.id}
                      className="inline-flex items-center gap-1.5 font-label text-sm font-medium text-red-500 hover:text-red-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-100/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {deleting === post.id ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-delete-bin-line" />
                      )}
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}